import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import React, { lazy, Suspense } from "react";
import { MobileCallButton } from "./components/MobileCallButton";

const Index = lazy(() => import("./pages/Index"));
const About = lazy(() => import("./pages/About"));
const Services = lazy(() => import("./pages/Services"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail"));
const Fleet = lazy(() => import("./pages/Fleet"));
const VehicleDetail = lazy(() => import("./pages/VehicleDetail"));
const Contact = lazy(() => import("./pages/Contact"));

const Destinations = lazy(() => import("./pages/Destinations"));
const DestinationDetail = lazy(() => import("./pages/DestinationDetail"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const SeoPage = lazy(() => import("./pages/SeoPage"));
const HotelTransportation = lazy(() => import("./pages/HotelTransportation"));
const NotFound = lazy(() => import("./pages/NotFound"));
const queryClient = new QueryClient();

// 301-equivalent redirects for old/renamed URLs.
// Kept in a single map so every entry maps to a live, verified page.
const REDIRECTS: Record<string, string> = {
  // Old / renamed blog slugs
  "/blog/best-transportation-for-weddings-in-naples-florida":
    "/blog/luxury-transportation-weddings-naples",
  "/blog/best-transportation-for-florida-vacationers":
    "/blog/best-transportation-florida-vacationers",
  // Blog paths reached without the /blog/ prefix
  "/best-transportation-families-traveling-florida":
    "/blog/best-transportation-families-traveling-florida",
  "/naples-to-port-miami": "/blog/naples-to-port-miami",
  "/naples-to-miami-airport": "/blog/naples-to-miami-international-airport",
  "/naples-airport-transportation": "/blog/naples-airport-transportation-guide",
  // Destination routes that were never real routes -> travel guides
  "/destinations/marco-island": "/blog/luxury-travel-guide-marco-island",
  "/destinations/bonita-springs": "/blog/luxury-travel-guide-bonita-springs",
  // Service slug mismatches
  "/services/airport-transportation": "/services/airport-transfer",
  "/services/long-distance": "/services",
  // Fleet slug mismatch
  "/fleet/platinum-suv-7-passengers": "/fleet/cadillac-escalade-suv",
  // Contact URL typo
  "/contact-us": "/contact",
  // Team page (does not exist) -> About
  "/team": "/about",
  // Service-area slug mismatches / cruise-port naming
  "/service-areas/naples-to-port-of-miami":
    "/service-areas/naples-to-port-miami",
  "/service-areas/naples-to-fort-lauderdale-cruise-port":
    "/service-areas/naples-to-port-everglades",
  // Blog slug mismatch
  "/blog/best-executive-car-service-in-naples":
    "/blog/best-executive-car-service-naples",
};

const RedirectRoute = ({ to }: { to: string }) => {
  React.useEffect(() => {
    // Best-effort signaling for search engines / tools that honor JS redirects
    try {
      window.history.replaceState(null, "", to);
    } catch {
      /* noop */
    }
  }, [to]);
  return <Navigate to={to} replace />;
};

const App = () => {
  React.useEffect(() => {
    const handleExternalLinks = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a");

      if (anchor && anchor.href) {
        const url = new URL(anchor.href, window.location.href);

        // If it's an http/https link and not the same origin
        if (
          (url.protocol === "http:" || url.protocol === "https:") &&
          url.origin !== window.location.origin
        ) {
          e.preventDefault();
          window.open(anchor.href, "_blank", "noopener,noreferrer");
        }
      }
    };

    document.addEventListener("click", handleExternalLinks);
    return () => document.removeEventListener("click", handleExternalLinks);
  }, []);

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Suspense
              fallback={
                <div className="min-h-screen bg-background flex items-center justify-center">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              }
            >
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/about" element={<About />} />
                <Route path="/services" element={<Services />} />
                <Route path="/services/:slug" element={<ServiceDetail />} />
                <Route path="/fleet" element={<Fleet />} />
                <Route path="/fleet/:slug" element={<VehicleDetail />} />
                <Route path="/destinations" element={<Destinations />} />
                <Route
                  path="/destinations/:slug"
                  element={<DestinationDetail />}
                />
                <Route path="/contact" element={<Contact />} />

                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/service-areas/:slug" element={<SeoPage />} />

                {/* Legacy / renamed URL redirects — must stay above catch-all */}
                {Object.entries(REDIRECTS).map(([from, to]) => (
                  <Route
                    key={from}
                    path={from}
                    element={<RedirectRoute to={to} />}
                  />
                ))}

                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="/book" element={<Navigate to="/" replace />} />
                <Route path="/booking" element={<Navigate to="/" replace />} />
                <Route path="/:slug" element={<HotelTransportation />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
              <MobileCallButton />
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
